<template>
  <!-- ========== Left Sidebar Start ========== -->
  <div class="vertical-menu">
    <simplebar :settings="settings" class="side-nav" ref="currentMenu" id="my-element">
      <SideNav :items="menuItems" />
    </simplebar>
    <!-- ========== Contextuel Sidebar ========== -->
    <Transition name="slide">
      <simplebar
        v-if="contextualMenuItems.length > 0"
        :settings="settings"
        class="side-nav-overlay"
        ref="currentMenu"
        id="my-element"
      >
        <SideNav
          :items="contextualMenuItems"
          :is-contextual-side-nav="true"
          :back-route-link="contextualNavReturnRoute"
          :back-route-label="contextualNavReturnLabel"
        />
      </simplebar>
    </Transition>
  </div>
</template>

<script>
import { menuItems } from './menu';
import simplebar from 'simplebar-vue';
import SideNav from './side-nav';
import { mapGetters, mapState } from 'vuex';
/**
 * Sidebar component
 */
export default {
  components: { simplebar, SideNav },
  props: {
    type: {
      type: String,
      required: true,
    },
    width: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      settings: {
        minScrollbarLength: 60,
      },
      menuItems: menuItems,
      contextualMenuItems: [],
      contextualNavReturnRoute: null,
      currentParent: null,
      contextualNavReturnLabel: null,
    };
  },
  computed: {
    ...mapGetters('currentUser', {
      currentSite: 'currentSite',
    }),
    ...mapState('layout', ['breadcrumbs']),
    breadcrumb() {
      if (typeof this.$route.meta.breadcrumb !== 'function') return null;

      return this.$route.meta.breadcrumb(this.$route);
    },
  },
  methods: {
    onRoutechange() {
      setTimeout(() => {
        if (document.getElementsByClassName('mm-active').length > 0) {
          const currentPosition = document.getElementsByClassName('mm-active')[0].offsetTop;
          if (currentPosition > 500)
            if (this.$refs.isSimplebar)
              this.$refs.isSimplebar.value.getScrollElement().scrollTop = currentPosition + 300;
        }
      }, 300);
    },
    // refresh active elements in the sidebar based on the current route
    refreshSideNavActiveElements() {
      let currentMenuItem = null;
      let contextualParentMenuItem = null;
      const activeParents = [];

      // Helper: find a menu item whose link name exactly matches the current route.
      const findExactMatch = (items, parents = []) => {
        for (const item of items) {
          if (item.link?.name && this.$route.name === item.link.name) {
            return { item, parents, contextualParent: parents.find(p => p.contextualSubItems) || null };
          }
          if (item.subItems || item.contextualSubItems) {
            const subItems = item.subItems ?? item.contextualSubItems;
            const result = findExactMatch(subItems, [...parents, item]);
            if (result) return result;
          }
        }
        return null;
      };

      // Helper: find a menu item that matches one of the breadcrumb route names.
      const findBreadcrumbMatch = (items, parents = []) => {
        for (const item of items) {
          if (item.link?.name && this.breadcrumb?.some(b => b.routeName === item.link.name)) {
            return { item, parents, contextualParent: parents.find(p => p.contextualSubItems) || null };
          }
          if (item.subItems || item.contextualSubItems) {
            const subItems = item.subItems ?? item.contextualSubItems;
            const result = findBreadcrumbMatch(subItems, [...parents, item]);
            if (result) return result;
          }
        }
        return null;
      };

      // First try to find an exact match.
      const exactResult = findExactMatch(this.menuItems);
      if (exactResult) {
        currentMenuItem = exactResult.item;
        activeParents.push(...exactResult.parents);
        contextualParentMenuItem = exactResult.contextualParent;
      } else {
        // If no exact match, fallback to breadcrumb matching.
        const breadcrumbResult = findBreadcrumbMatch(this.menuItems);
        if (breadcrumbResult) {
          currentMenuItem = breadcrumbResult.item;
          activeParents.push(...breadcrumbResult.parents);
          contextualParentMenuItem = breadcrumbResult.contextualParent;
        }
      }

      // Setup contextual side nav if needed.
      this.contextualMenuItems = contextualParentMenuItem?.contextualSubItems ?? [];
      if (this.contextualMenuItems.length > 0) {
        this.contextualNavReturnRoute = contextualParentMenuItem?.link;
        this.contextualNavReturnLabel =
          contextualParentMenuItem?.backRouteLabel ??
          this.$t('menuItems.goBack', [this.$t(contextualParentMenuItem?.label)]);
      }

      // Reset current active states.
      this.activeParents?.forEach(parent => {
        parent.opened = false;
        parent.active = false;
      });
      if (this.currentMenuItem) this.currentMenuItem.active = false;

      // Activate the parent chain and the current item.
      activeParents.forEach(parent => {
        parent.opened = true;
        parent.active = true;
      });
      if (currentMenuItem) currentMenuItem.active = true;

      // Save state for future updates.
      this.activeParents = activeParents;
      this.currentMenuItem = currentMenuItem;
    },
  },
  watch: {
    $route: {
      handler: 'onRoutechange',
      immediate: true,
      deep: true,
    },
    $route: {
      handler: 'refreshSideNavActiveElements',
      immediate: true,
    },
    currentSite: {
      handler: 'refreshSideNavActiveElements',
    },
    type: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          switch (newVal) {
            case 'dark':
              document.body.setAttribute('data-sidebar', 'dark');
              document.body.removeAttribute('data-topbar');
              document.body.removeAttribute('data-sidebar-size');
              document.body.removeAttribute('data-keep-enlarged');
              document.body.classList.remove('vertical-collpsed');
              break;
            case 'light':
              document.body.setAttribute('data-topbar', 'dark');
              document.body.removeAttribute('data-sidebar');
              document.body.removeAttribute('data-sidebar-size');
              //document.body.classList.remove('vertical-collpsed');
              break;
            case 'compact':
              document.body.setAttribute('data-sidebar-size', 'small');
              document.body.setAttribute('data-sidebar', 'dark');
              //document.body.classList.remove('vertical-collpsed');
              document.body.removeAttribute('data-topbar', 'dark');
              break;
            case 'icon':
              document.body.setAttribute('data-keep-enlarged', 'true');
              document.body.classList.add('vertical-collpsed');
              document.body.setAttribute('data-sidebar', 'dark');
              document.body.removeAttribute('data-topbar', 'dark');
              break;
            case 'colored':
              document.body.setAttribute('data-sidebar', 'colored');
              document.body.removeAttribute('data-keep-enlarged');
              //document.body.classList.remove('vertical-collpsed');
              document.body.removeAttribute('data-sidebar-size');
              break;
            default:
              document.body.setAttribute('data-sidebar', 'dark');
              break;
          }
        }
      },
    },
    width: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          switch (newVal) {
            case 'boxed':
              document.body.setAttribute('data-layout-size', 'boxed');
              document.body.removeAttribute('data-layout-scrollable');
              break;
            case 'fluid':
              document.body.setAttribute('data-layout-mode', 'fluid');
              document.body.removeAttribute('data-layout-size');
              document.body.removeAttribute('data-layout-scrollable');
              break;
            case 'scrollable':
              document.body.setAttribute('data-layout-scrollable', 'true');
              document.body.removeAttribute('data-layout-mode');
              document.body.removeAttribute('data-layout-size');
              break;
            default:
              document.body.setAttribute('data-layout-mode', 'fluid');
              break;
          }
        }
      },
    },
    mode: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          switch (newVal) {
            case 'light':
              document.body.setAttribute('data-bs-theme', 'light');
              break;
            case 'dark':
              document.body.setAttribute('data-bs-theme', 'dark');
              break;
            default:
              document.body.setAttribute('data-bs-theme', 'light');
              break;
          }
        }
      },
    },
  },
};
</script>
<style lang="scss" scoped>
.side-nav {
  height: 100%;
  &-overlay {
    @extend .side-nav;
    position: absolute;
    width: 100%;
    top: 0px;
    background: white;
  }
}

.slide-enter-active {
  transition: all 0.3s ease-out;
  box-shadow: 0.32rem 0.32rem 0.94rem rgb(18 38 63 / 7%);
}

.slide-leave-active {
  transition: all 0.3s ease-in;
  box-shadow: 0.32rem 0.32rem 0.94rem rgb(18 38 63 / 7%);
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
